import {
    ArrowForwardOutlined as ArrowForwardOutlinedIcon,
    CheckOutlined as CheckOutlinedIcon,
    OpenInNew as OpenInNewIcon,
} from 'components/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { sortByAccessor } from 'components/tables/list-table/sortUtil';
import cloneDeep from 'lodash/cloneDeep';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useFindMatterRatesQuery } from 'store/api/matters';
import { useFindPricelistRatesQuery, useFindPricelistsByClientQuery } from 'store/api/pricelists';
import { UpdateHourlyRatesDto } from 'types/matters';
import { CalculatedPricelist, HourlyRate, PricingKind, ScopeType } from 'types/pricelists';
import { cn } from 'utils/cn';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations';
import { PricelistDetailsFetchingCard } from '../PricelistDetailsFetchingCard';
import { roleRatesOf } from './employeePricing';
import {
    DEFAULT_QUICK_FILTERS,
    PickerViewOptions,
    PricelistsPickerToolbar,
    QUICK_FILTERS,
} from './PricelistsPickerToolbar';
import { addUniquePricelistsToOrderedList, isOfferedInPicker, pricelistIdentity } from '../util';
import { FollowablePricelistCard } from './FollowablePricelistCard';

// The sections the picker lists its cards in, widest reach first: the firm's own model lists, then the
// client's contract lists, then the lists that belong to one case. These cover every card the ordering
// below can produce — it only ever collects these four scope codes.
const PICKER_GROUPS: { labelKey: string; scopes: string[] }[] = [
    { labelKey: 'pricelists.picker.group-model', scopes: [ScopeType.COMPANY, ScopeType.TENANT] },
    { labelKey: 'pricelists.picker.group-contract', scopes: [ScopeType.CONTRACT] },
    { labelKey: 'pricelists.picker.group-case', scopes: [ScopeType.MATTER] },
];

interface Props {
    clientId?: number;
    pricelistData: UpdateHourlyRatesDto;
    onChange: (value: UpdateHourlyRatesDto) => void;
    currencyTypeCode?: string | null;
    serviceAgreementContractId?: number | null;
    setIsLoadingData: (isLoading: boolean) => void;
    // An employee-priced case follows the list it picks instead of copying its prices.
    pricingKind?: PricingKind;
    followedPricelistId?: number | null;
    onFollow?: (pricelist: CalculatedPricelist) => void;
}

export const PricelistsCarousel = ({
    clientId,
    pricelistData,
    onChange,
    currencyTypeCode,
    serviceAgreementContractId,
    setIsLoadingData,
    pricingKind = PricingKind.ROLE,
    followedPricelistId,
    onFollow,
}: Props): ReactNode => {
    const [viewOptions, setViewOptions] = useState<PickerViewOptions>({
        search: '',
        sort: '',
        filters: DEFAULT_QUICK_FILTERS,
    });
    // A case card stands for one list of a case, so the selection is the list id and the rows are picked
    // out of that case's rates read.
    const [matterId, setMatterId] = useState<number | undefined>(undefined);
    const [matterPricelistId, setMatterPricelistId] = useState<number | undefined>(undefined);
    const [pricelistId, setPricelistId] = useState<number | undefined>(
        pricelistData.referencedPricelist.id || undefined
    );
    const { data: pricelistRates, isFetching: isFetchingPricelistRates } = useFindPricelistRatesQuery(
        pricelistId?.toString() ?? skipToken
    );
    const { data: matterPricelists, isFetching: isFetchingMatterRates } = useFindMatterRatesQuery(
        matterId?.toString() ?? skipToken
    );
    const matterRates = useMemo(
        () => matterPricelists?.find((list) => list.id === matterPricelistId)?.rates,
        [matterPricelists, matterPricelistId]
    );

    const { data: pricelistsList, isLoading: isLoadingPricelists } = useFindPricelistsByClientQuery(
        clientId?.toString() || 'model'
    );

    // TODO: react-doctor/no-prop-callback-in-effect + no-derived-state-effect —
    // propagating RTK Query fetching flags to the parent. Lifting the queries
    // up or reading them via context would be the proper fix.
    useEffect(() => {
        setIsLoadingData(isFetchingMatterRates || isFetchingPricelistRates);
    }, [isFetchingMatterRates, isFetchingPricelistRates, setIsLoadingData]);

    const mapHourlyRatesFromSelectedPricelist = useCallback(
        (matterPricelistId: number | null, pricelistId: number | null, hourlyRates: HourlyRate[]) => {
            const res = cloneDeep(pricelistData);
            res.referencedPricelist = {
                id: pricelistId,
                pricingHash: pricelistsList?.find((p) =>
                    matterPricelistId != null ? p.matterPricelistId === matterPricelistId : p.id === pricelistId
                )?.pricingHash,
            };
            // Prefill replaces the whole role grid, so the open tab ends up showing the source list and
            // nothing else: a role the source does not price is left empty rather than keeping whatever
            // number happened to be there, which reads as if the source had supplied it.
            // A price of 0 is a price — optional chaining keeps it, only a null/absent row clears the row.
            res['rates'] = pricelistData.rates.map((rate) => ({
                ...rate,
                hourlyPrice:
                    hourlyRates
                        ?.find((hourlyRate) => hourlyRate.jobRole.id === rate.jobRole.id)
                        ?.hourlyPrice?.toString() ?? '',
            }));
            onChange(res);
        },
        [pricelistsList, pricelistData, onChange]
    );

    // TODO: react-doctor/no-effect-chain — these effects react to RTK Query
    // results and forward computed data via the onChange prop. Restructuring
    // would require moving the queries into the parent or using a custom hook.
    useEffect(() => {
        if (
            pricelistId &&
            pricelistRates &&
            !isFetchingPricelistRates &&
            pricelistId !== pricelistData.referencedPricelist.id
        ) {
            mapHourlyRatesFromSelectedPricelist(null, pricelistId, roleRatesOf(pricelistRates));
        }
    }, [
        mapHourlyRatesFromSelectedPricelist,
        pricelistData.referencedPricelist.id,
        pricelistId,
        pricelistRates,
        isFetchingPricelistRates,
    ]);

    useEffect(() => {
        if (!matterPricelistId || !matterRates || isFetchingMatterRates) return;
        // Skip re-propagation when pricelistData already reflects this list's pricing.
        // Without this guard, mapHourlyRatesFromSelectedPricelist's identity flips on every
        // onChange (it closes over pricelistData), re-firing this effect and looping.
        const expectedPricingHash = pricelistsList?.find((p) => p.matterPricelistId === matterPricelistId)?.pricingHash;
        const alreadySynced =
            pricelistData.referencedPricelist.id === null &&
            pricelistData.referencedPricelist.pricingHash === expectedPricingHash;
        if (!alreadySynced) {
            mapHourlyRatesFromSelectedPricelist(matterPricelistId, null, matterRates);
        }
    }, [
        matterPricelistId,
        matterRates,
        isFetchingMatterRates,
        mapHourlyRatesFromSelectedPricelist,
        pricelistsList,
        pricelistData.referencedPricelist.id,
        pricelistData.referencedPricelist.pricingHash,
    ]);

    const renderPricelist = useCallback(
        (key: string, pricelist: CalculatedPricelist): JSX.Element => {
            if (pricingKind === PricingKind.EMPLOYEE) {
                return (
                    <FollowablePricelistCard
                        key={key}
                        pricelist={pricelist}
                        isSelected={pricelist.id === followedPricelistId}
                        onFollow={(picked) => onFollow?.(picked)}
                    />
                );
            }
            const isMatterPricelist = pricelist.scopeTypeCode === ScopeType.MATTER;
            const { referencedPricelist } = pricelistData;

            let isSelected = false;
            if (pricelistId) {
                isSelected = pricelist.id === pricelistId;
            } else if (matterPricelistId) {
                isSelected = pricelist.matterPricelistId === matterPricelistId;
            } else {
                isSelected = pricelist.pricingHash === referencedPricelist.pricingHash;
            }

            const pick = () => {
                if (isMatterPricelist) {
                    setMatterId(pricelist.matterId);
                    setMatterPricelistId(pricelist.matterPricelistId);
                    setPricelistId(undefined);
                } else {
                    setMatterId(undefined);
                    setMatterPricelistId(undefined);
                    setPricelistId(pricelist.id);
                }
            };

            return (
                <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    onClick={pick}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            pick();
                        }
                    }}
                    className={cn(
                        'hover:bg-primary-surface hover:text-primary hover:border-primary/40 mt-2.5 flex min-h-[50px] cursor-pointer items-center rounded-[5px] border px-2.5 py-2',
                        isSelected ? 'border-primary bg-primary-surface' : 'border-grey-300 bg-paper'
                    )}
                >
                    <div className="w-11/12">
                        <PricelistDetailsFetchingCard pricelist={pricelist} whiteSpace="nowrap" />
                    </div>
                    <div className="flex w-1/12 justify-end">
                        {isSelected ? (
                            <CheckOutlinedIcon />
                        ) : (
                            <ArrowForwardOutlinedIcon className="text-grey-300 text-[20px]" />
                        )}
                    </div>
                </div>
            );
        },
        [pricelistData, pricelistId, matterPricelistId, pricingKind, followedPricelistId, onFollow]
    );

    const getPricelistsInRightOrder = useCallback(
        (pricelists: CalculatedPricelist[]) => {
            const orderedList: CalculatedPricelist[] = [];
            const serviceAgreementContractPricelist = pricelists.find(
                (p) => p.contractId === serviceAgreementContractId
            );

            if (serviceAgreementContractPricelist) {
                orderedList.push(serviceAgreementContractPricelist);
            }

            addUniquePricelistsToOrderedList(
                pricelists.filter((p) => p.scopeTypeCode === ScopeType.CONTRACT),
                orderedList
            );
            addUniquePricelistsToOrderedList(
                pricelists.filter((p) => p.scopeTypeCode === ScopeType.COMPANY || p.scopeTypeCode === ScopeType.TENANT),
                orderedList
            );
            addUniquePricelistsToOrderedList(
                pricelists.filter((p) => p.scopeTypeCode === ScopeType.MATTER),
                orderedList
            );

            return orderedList;
        },
        [serviceAgreementContractId]
    );

    // Name search and sort run on the *output* of getPricelistsInRightOrder, i.e. after the
    // pricingHash dedupe. Reordering or filtering its input would change which of the rows
    // sharing a pricingHash survives, and with it the pricelist id sent on save — the card
    // would then no longer stand for the pricelist the backend applies.
    const visiblePricelists = useMemo(() => {
        const ordered = getPricelistsInRightOrder(
            (pricelistsList ?? []).filter((pricelist) => isOfferedInPicker(pricelist, pricingKind, currencyTypeCode))
        );
        const query = foldForSearch(viewOptions.search.trim());
        const matching = query
            ? ordered.filter((pricelist) => foldForSearch(pricelist.name ?? '').includes(query))
            : ordered;
        const pressed = QUICK_FILTERS.filter((filter) => viewOptions.filters.includes(filter.value));
        const narrowed = pressed.length
            ? matching.filter((pricelist) => pressed.every((filter) => filter.matches(pricelist)))
            : matching;
        const [, direction] = viewOptions.sort.split(',');
        return direction ? sortByAccessor(narrowed, (pricelist) => pricelist.name, direction === 'desc') : narrowed;
    }, [getPricelistsInRightOrder, pricelistsList, currencyTypeCode, viewOptions, pricingKind]);

    // Grouping runs after the ordering and the search, so the sections only change how the surviving
    // cards are laid out — never which of the cards sharing a pricingHash survived.
    const pricelistGroups = useMemo(
        () =>
            PICKER_GROUPS.map((group) => ({
                labelKey: group.labelKey,
                pricelists: visiblePricelists.filter((pricelist) => group.scopes.includes(pricelist.scopeTypeCode)),
            })).filter((group) => group.pricelists.length > 0),
        [visiblePricelists]
    );

    if (isLoadingPricelists) {
        return <Loader />;
    }

    const perEmployee = pricingKind === PricingKind.EMPLOYEE;
    const noFollowableList =
        perEmployee &&
        !(pricelistsList ?? []).some((pricelist) => isOfferedInPicker(pricelist, pricingKind, currencyTypeCode));
    if (noFollowableList) {
        return (
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm">
                <p className="text-grey-500 text-pretty">{t('pricelists.case-editor.no-employee-models')}</p>
                {/* A new tab, so the case form behind this window keeps what was entered. */}
                <RouterLink
                    className="text-link inline-flex items-center gap-1 hover:underline"
                    to="/pricelists"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('pricelists.case-editor.open-pricelists')}
                    <OpenInNewIcon fontSize="small" />
                </RouterLink>
            </div>
        );
    }

    if (!pricelistsList?.length) {
        return null;
    }

    return (
        <>
            <PricelistsPickerToolbar viewOptions={viewOptions} onChange={setViewOptions} />
            {pricelistGroups.length ? (
                pricelistGroups.map((group, index) => (
                    <div key={group.labelKey} className={cn(index > 0 && 'pt-2.5')}>
                        <div className="pb-0.5 text-[0.85rem] font-semibold">{t(group.labelKey)}</div>
                        {group.pricelists.map((pricelist: CalculatedPricelist) =>
                            renderPricelist(pricelistIdentity(pricelist), pricelist)
                        )}
                    </div>
                ))
            ) : (
                <div className="text-grey-500 py-6 text-center text-sm">{t('pricelists.no-pricelists-found')}</div>
            )}
        </>
    );
};
